/** @format */

import {
  faBookOpen,
  faFileCertificate,
  faFileLines,
  faImage,
  faStamp,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import NewTooltip from '../../../misc/NewTooltip';
import ImagePreviewDialog from './ImagePreviewDialog';
import ProductDocumentsDialog from './ProductDocumentsDialog';

export const PRODUCT_DOC_ICONS = [
  { type: 'data_sheets', icon: faFileLines, tooltip: 'Fiches techniques' },
  { type: 'warranties', icon: faFileCertificate, tooltip: 'Garanties' },
  {
    type: 'conformity_certificates',
    icon: faStamp,
    tooltip: 'Certificats de conformité',
  },
  { type: 'user_manuals', icon: faBookOpen, tooltip: 'Manuels utilisateur' },
  { type: 'image', icon: faImage, tooltip: 'Image' },
];

export function productHasDocument(product, docType) {
  if (!product) return false;
  if (docType === 'image') {
    return Boolean(product.image_url || product.image);
  }
  const items = product[docType];
  return Array.isArray(items) && items.length > 0;
}

export function productsHaveDocument(documentProducts, docType) {
  return (documentProducts || []).some(({ product }) =>
    productHasDocument(product, docType),
  );
}

function resolveDocumentProducts(product, productName, childProducts) {
  if (childProducts?.length) {
    return childProducts;
  }
  if (product?.id) {
    return [
      {
        product,
        label:
          productName ||
          product.public_name ||
          product.custom_public_name ||
          product.prefixed_internal_name ||
          product.custom_internal_name ||
          product.internal_name ||
          '',
      },
    ];
  }
  return [];
}

function ProductImagePickerDialog({
  open,
  onClose,
  options,
  onSelect,
  zIndex,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={zIndex != null ? { zIndex } : undefined}
    >
      <DialogTitle>Choisir une image</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <List dense>
          {options.map((option) => (
            <ListItemButton
              key={`${option.label}-${option.url}`}
              onClick={() => onSelect(option.url)}
            >
              <ListItemText primary={option.label} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}

ProductImagePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  zIndex: PropTypes.number,
};

ProductImagePickerDialog.defaultProps = {
  zIndex: null,
};

export default function ProductDocumentActionButtons({
  product,
  productName,
  childProducts,
  iconButtonSize,
  dialogZIndex,
}) {
  const [docDialog, setDocDialog] = useState({
    open: false,
    docType: null,
  });
  const [imagePreview, setImagePreview] = useState({
    open: false,
    url: null,
  });
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const documentProducts = useMemo(
    () => resolveDocumentProducts(product, productName, childProducts),
    [product, productName, childProducts],
  );

  if (!documentProducts.length) return null;

  const dialogTitle =
    productName ||
    (documentProducts.length === 1 ? documentProducts[0].label : 'Kit');

  const handleOpenDocs = (docType) => {
    if (docType === 'image') {
      const imageOptions = documentProducts
        .map(({ product: rowProduct, label }) => ({
          label,
          url: rowProduct.image_url || rowProduct.image || '',
        }))
        .filter((option) => option.url);

      if (imageOptions.length === 0) return;
      if (imageOptions.length === 1) {
        setImagePreview({ open: true, url: imageOptions[0].url });
        return;
      }
      setImagePickerOpen(true);
      return;
    }
    setDocDialog({
      open: true,
      docType,
    });
  };

  const imagePickerOptions = documentProducts
    .map(({ product: rowProduct, label }) => ({
      label,
      url: rowProduct.image_url || rowProduct.image || '',
    }))
    .filter((option) => option.url);

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-start"
        flexShrink={0}
        sx={{ ml: 0.5 }}
      >
        {PRODUCT_DOC_ICONS.map(({ type, icon, tooltip }) => {
          const hasItems = productsHaveDocument(documentProducts, type);
          return (
            <NewTooltip key={type} content={tooltip}>
              <IconButton
                size={iconButtonSize}
                onClick={() => handleOpenDocs(type)}
                color={hasItems ? 'primary' : 'default'}
              >
                <FontAwesomeIcon icon={icon} />
              </IconButton>
            </NewTooltip>
          );
        })}
      </Box>

      {docDialog.open && docDialog.docType ? (
        <ProductDocumentsDialog
          open={docDialog.open}
          onClose={() => setDocDialog({ open: false, docType: null })}
          products={documentProducts.map(({ product: rowProduct, label }) => ({
            id: rowProduct.id,
            name: label,
          }))}
          docType={docDialog.docType}
          productName={dialogTitle}
          zIndex={dialogZIndex}
        />
      ) : null}

      {imagePickerOpen ? (
        <ProductImagePickerDialog
          open={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          options={imagePickerOptions}
          zIndex={dialogZIndex}
          onSelect={(url) => {
            setImagePickerOpen(false);
            setImagePreview({ open: true, url });
          }}
        />
      ) : null}

      {imagePreview.open && imagePreview.url ? (
        <ImagePreviewDialog
          open={imagePreview.open}
          onClose={() => setImagePreview({ open: false, url: null })}
          url={imagePreview.url}
          zIndex={dialogZIndex}
        />
      ) : null}
    </>
  );
}

ProductDocumentActionButtons.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    image: PropTypes.string,
    image_url: PropTypes.string,
    public_name: PropTypes.string,
    custom_public_name: PropTypes.string,
    prefixed_internal_name: PropTypes.string,
    custom_internal_name: PropTypes.string,
    internal_name: PropTypes.string,
    data_sheets: PropTypes.arrayOf(PropTypes.shape({})),
    warranties: PropTypes.arrayOf(PropTypes.shape({})),
    conformity_certificates: PropTypes.arrayOf(PropTypes.shape({})),
    user_manuals: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  productName: PropTypes.string,
  childProducts: PropTypes.arrayOf(
    PropTypes.shape({
      product: PropTypes.shape({
        id: PropTypes.number,
      }),
      label: PropTypes.string,
    }),
  ),
  iconButtonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  dialogZIndex: PropTypes.number,
};

ProductDocumentActionButtons.defaultProps = {
  product: null,
  productName: '',
  childProducts: null,
  iconButtonSize: 'small',
  dialogZIndex: null,
};
